/*
 * NextGenn engine · the scroll film (sections/ngs-film.liquid).
 *
 * The page arrives as the STILL scene: a poster, the word, every line, the
 * button. This element upgrades it to the pinned stage, where the scroll
 * position is the film's clock, and hands it back the moment that stops being
 * true (no film, a failed load, motion switched off).
 *
 * What it is careful about, each one a defect somebody has already had on the
 * coded Loz:
 *
 *  · ONE seek at a time. Writing `currentTime` on every frame is why scroll
 *    films stutter: seeks pile up faster than the decoder finishes them and the
 *    picture never lands. While the browser says `seeking`, the loop waits, and
 *    the next seek goes straight to wherever the scroll is by then.
 *  · It cannot wedge. A seek the browser drops would hold that gate shut for
 *    the rest of the visit, so a seek older than SEEK_PATIENCE_MS is overruled.
 *  · It never calls `fastSeek` (it snaps to keyframes, one every half second on
 *    these encodes: a slideshow) and never reads `buffered` (Safari reports
 *    nothing it holds, so clamping to it pins the film to its first second).
 *  · The playhead is EASED toward the scroll, so a flick of the thumb is a
 *    short glide through the film and not a jump cut.
 *  · Nothing runs off screen: the file is asked for one screen before the
 *    section arrives (and never before the visitor's first touch, scroll or
 *    mouse move), the loop lives only while the section is in view.
 *  · The scroll range is measured from the stage itself, not `innerHeight`,
 *    which changes every time a phone's address bar hides or returns.
 */
if (!customElements.get('ngs-film')) {
  /* One frame of the films, in seconds. They are cut at 24 a second; aiming
     closer than half a frame cannot change the picture, so it is not a seek. */
  const FRAME = 1 / 24;
  /* How much of the gap to the scroll closes per 60 Hz frame. */
  const EASE = 0.16;
  const SEEK_PATIENCE_MS = 400;
  /* The button rises for the last 15% of the film. */
  const CTA_AT = 0.85;

  const clamp = (value) => Math.min(1, Math.max(0, value));
  const watch = (query, handler) => {
    if (query.addEventListener) query.addEventListener('change', handler);
  };
  const unwatch = (query, handler) => {
    if (query.removeEventListener) query.removeEventListener('change', handler);
  };

  customElements.define(
    'ngs-film',
    class NgsFilm extends HTMLElement {
      /* A visitor who asked their browser to save data, or is on 2G, gets the
         still scene: two to three megabytes of film is not ours to spend. */
      static saving() {
        const connection = navigator.connection;
        return Boolean(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')));
      }

      connectedCallback() {
        this.stage = this.querySelector('.ngs-film__stage');
        this.video = this.querySelector('video');
        this.cta = this.querySelector('.ngs-film__cta');
        this.line = this.querySelector('.ngs-film__line');
        this.beats = Array.from(this.querySelectorAll('.ngs-film__beat')).map((el) => {
          const from = Number(el.dataset.from) || 0;
          const to = Number(el.dataset.to) || 0;
          return { el, from, to: to > from ? to : 100, on: false };
        });

        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.wide = window.matchMedia('(min-width: 750px)');
        if (!this.stage || !this.video || !this.source().src) return;
        if (this.reduced.matches || NgsFilm.saving()) return;

        this.shown = null;
        this.painted = -1;
        this.ctaOn = false;
        this.running = false;
        this.frame = 0;
        this.stamp = 0;
        this.seekAt = 0;
        this.current = '';
        this.primed = false;

        this.tick = this.tick.bind(this);
        this.prime = this.prime.bind(this);
        this.onError = () => this.toStatic();
        this.onMeta = () => this.prime();
        this.onPicture = () => this.video.setAttribute('data-ready', '');
        this.onBreak = () => {
          if (this.current) this.attach();
        };
        this.onReduced = () => {
          if (this.reduced.matches) this.toStatic();
        };
        this.onBlockSelect = this.onBlockSelect.bind(this);

        this.video.addEventListener('error', this.onError);
        this.video.addEventListener('loadedmetadata', this.onMeta);
        ['loadeddata', 'seeked', 'playing'].forEach((name) => this.video.addEventListener(name, this.onPicture));
        watch(this.wide, this.onBreak);
        watch(this.reduced, this.onReduced);
        this.addEventListener('shopify:block:select', this.onBlockSelect);

        this.live = true;
        this.dataset.mode = 'pinned';

        /* Ask for the film one screen before the section arrives, and not
           before somebody is there (window.ngsIntent, layout/theme.liquid).
           Right under the hero, "one screen before" is the moment the page
           opens, and 1.7 MB of film was racing the first photograph. */
        this.loader = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.loader.disconnect();
            this.attach();
          },
          { rootMargin: '100% 0px' }
        );
        (window.ngsIntent || Promise.resolve()).then(() => {
          if (this.live) this.loader.observe(this);
        });

        this.runner = new IntersectionObserver(
          (entries) => {
            const visible = entries.some((entry) => entry.isIntersecting);
            if (visible) this.start();
            else this.stop();
          },
          { rootMargin: '10% 0px' }
        );
        this.runner.observe(this);
      }

      disconnectedCallback() {
        this.teardown();
      }

      /* A phone gets the tall file, a laptop the wide one; either stands in for
         the other when only one was given. */
      source() {
        const wide = this.wide.matches;
        const data = this.dataset;
        return {
          src: (wide ? data.wide : data.tall) || data.wide || data.tall || '',
          poster: (wide ? data.posterWide : data.posterTall) || data.posterWide || data.posterTall || '',
        };
      }

      attach() {
        const { src, poster } = this.source();
        if (!src) return this.toStatic();
        if (src === this.current) return;
        this.current = src;
        this.video.removeAttribute('data-ready');
        if (poster) this.video.poster = poster;

        /* THE FIRST SCREEN IS NOT THE FILM'S TO SPEND. On the home page this
           section is the second one, which means it is already within the one
           screen the observer waits for — so the fetch began during the first
           paint, and a megabyte and a half of film raced the hero photograph,
           the hero's own loop and the fonts for the same phone connection. The
           film is still asked for a screen early; it is asked for after the
           page has finished loading, and in an idle moment after that, so the
           things somebody is actually looking at get the line first. */
        const fetchIt = () => {
          /* The whole file: a scrub seeks everywhere, and a seek into a part
             that has not arrived is a network request with a finger waiting on
             it. */
          this.video.preload = 'auto';
          this.video.src = src;
          this.video.load();
        };
        const soon = () => {
          if ('requestIdleCallback' in window) window.requestIdleCallback(fetchIt, { timeout: 1500 });
          else window.setTimeout(fetchIt, 500);
        };
        if (document.readyState === 'complete') soon();
        else window.addEventListener('load', soon, { once: true });
        /* iOS will not show a seeked frame on a film that has never played.
           Muted and inline it may play unasked, so `loadedmetadata` tries; in
           Low Power Mode it may not, and the first touch is the permission. */
        window.addEventListener('touchstart', this.prime, { passive: true });
      }

      prime() {
        if (this.primed || !this.current) return;
        const attempt = this.video.play();
        if (!attempt || typeof attempt.then !== 'function') return;
        attempt
          .then(() => {
            this.primed = true;
            this.video.pause();
            window.removeEventListener('touchstart', this.prime);
          })
          .catch(() => {});
      }

      start() {
        if (this.running) return;
        this.running = true;
        this.stamp = 0;
        this.frame = requestAnimationFrame(this.tick);
      }

      stop() {
        this.running = false;
        cancelAnimationFrame(this.frame);
      }

      /* 0 when the stage pins, 1 when it lets go. */
      progress() {
        const rect = this.getBoundingClientRect();
        const range = rect.height - this.stage.offsetHeight;
        return range > 0 ? clamp(-rect.top / range) : 0;
      }

      tick(now) {
        if (!this.running) return;
        const target = this.progress();
        if (this.shown === null) {
          /* Opened half way down (a reload, a back button): start there. */
          this.shown = target;
        } else {
          /* The same glide on a 120 Hz screen as on a 60 Hz one. */
          const elapsed = this.stamp ? Math.min(100, now - this.stamp) : 16.7;
          const share = 1 - Math.pow(1 - EASE, elapsed / 16.7);
          const gap = target - this.shown;
          this.shown = Math.abs(gap) < 0.0005 ? target : this.shown + gap * share;
        }
        this.stamp = now;
        this.paint(this.shown);
        this.seek(this.shown);
        this.frame = requestAnimationFrame(this.tick);
      }

      /* The words follow the PICTURE (the eased value), not the raw scroll, so
         a line never arrives before the frame it is about. Written only on a
         change: this runs sixty times a second. */
      paint(progress) {
        if (progress === this.painted) return;
        this.painted = progress;
        if (this.line) this.line.style.setProperty('--ngs-film-p', progress.toFixed(4));

        const percent = progress * 100;
        this.beats.forEach((beat) => {
          const on = percent >= beat.from && (percent < beat.to || (beat.to >= 100 && percent >= 100));
          if (on === beat.on) return;
          beat.on = on;
          beat.el.toggleAttribute('data-on', on);
        });

        const ctaOn = progress >= CTA_AT;
        if (this.cta && ctaOn !== this.ctaOn) {
          this.ctaOn = ctaOn;
          this.cta.toggleAttribute('data-on', ctaOn);
        }
      }

      seek(progress) {
        const video = this.video;
        const duration = video.duration;
        if (video.readyState < 1 || !Number.isFinite(duration) || duration <= 0) return;
        /* Never the very end: some browsers show nothing at `duration` itself. */
        const time = Math.min(duration - FRAME, Math.max(0, progress * duration));
        if (Math.abs(video.currentTime - time) < FRAME / 2) return;
        const now = performance.now();
        if (video.seeking && now - this.seekAt < SEEK_PATIENCE_MS) return;
        this.seekAt = now;
        video.currentTime = time;
      }

      /* In the theme editor, choosing a line scrolls the film to the middle of
         that line's window, so the owner sees the words over the frame they
         will sit on instead of an invisible block. */
      onBlockSelect(event) {
        if (this.dataset.mode !== 'pinned') return;
        const beat = this.beats.find((item) => item.el === event.target);
        if (!beat) return;
        const rect = this.getBoundingClientRect();
        const range = rect.height - this.stage.offsetHeight;
        if (range <= 0) return;
        window.scrollTo({ top: window.scrollY + rect.top + (range * (beat.from + beat.to)) / 200 });
      }

      teardown() {
        if (!this.live) return;
        this.live = false;
        this.stop();
        if (this.loader) this.loader.disconnect();
        if (this.runner) this.runner.disconnect();
        this.video.removeEventListener('error', this.onError);
        this.video.removeEventListener('loadedmetadata', this.onMeta);
        ['loadeddata', 'seeked', 'playing'].forEach((name) => this.video.removeEventListener(name, this.onPicture));
        unwatch(this.wide, this.onBreak);
        unwatch(this.reduced, this.onReduced);
        this.removeEventListener('shopify:block:select', this.onBlockSelect);
        window.removeEventListener('touchstart', this.prime);
      }

      /* Back to the still scene: the poster stays, the lines become a list, the
         section is one screen tall again. The film is let go of, so a failed or
         unwanted download stops costing anything. */
      toStatic() {
        this.teardown();
        this.removeAttribute('data-mode');
        if (this.current) {
          this.current = '';
          this.video.pause();
          this.video.removeAttribute('src');
          this.video.removeAttribute('poster');
          this.video.load();
        }
      }
    }
  );
}
